import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WebSocketService, PanicAlertData } from './websocket.service';
import { AudioService } from './audio.service';
import { BrowserNotificationService } from './browser-notification.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { CentralistModeService } from './centralist-mode.service';

@Injectable({
  providedIn: 'root'
})
export class GlobalPanicAlertService {
  private activePanicAlertSubject = new BehaviorSubject<PanicAlertData | null>(null);
  public activePanicAlert$ = this.activePanicAlertSubject.asObservable();

  private processedAlerts = new Set<string>();
  private readonly PROCESSED_ALERTS_KEY = 'global_processed_panic_alerts';
  private facilitiesLoaded = false;
  private isLoadingFacilities = false;
  private initialized = false;
  private heartbeatInterval: any;

  constructor(
    private websocketService: WebSocketService,
    private audioService: AudioService,
    private browserNotificationService: BrowserNotificationService,
    private http: HttpClient,
    private centralistModeService: CentralistModeService
  ) {
    this.loadProcessedAlerts();
    this.listenForPanicAlerts();
  }

  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    this.heartbeatInterval = setInterval(() => {
      if (!this.isAuthenticated()) {
        console.log('💗 HEARTBEAT: Usuario no autenticado, deteniendo heartbeat');
        this.stopHeartbeat();
        return;
      }
      

      if (!this.websocketService.isConnected()) {
        console.log('💗 HEARTBEAT: Conexión perdida, reconectando...');
        this.ensureConnection();
      } else {
        console.log('💗 HEARTBEAT: Conexión activa');
      }
    }, 30000); // Check every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private loadFacilitiesAndConnect(): void {
    if (!this.isAuthenticated()) {
      console.log('🚫 GLOBAL: Usuario no autenticado, omitiendo carga de facilities');
      return;
    }

    // Solo cargar facilities si está en modo centralista
    if (!this.centralistModeService.isCentralistMode()) {
      console.log('🚫 GLOBAL: No estás en modo centralista, omitiendo carga de facilities');
      this.isLoadingFacilities = false;
      this.websocketService.connect();
      return;
    }

    if (this.facilitiesLoaded || this.isLoadingFacilities) {
      console.log('🏢 GLOBAL: Facilities ya cargadas o en proceso de carga, omitiendo');
      this.websocketService.connect();
      return;
    }

    console.log('🏢 GLOBAL: Cargando facilities...');
    this.isLoadingFacilities = true;


    // Always try to connect, even if facilities fail
    const connectWebSocket = () => {
      console.log('🔌 GLOBAL: Conectando WebSocket');
      this.websocketService.connect();
      this.isLoadingFacilities = false;
    };

    this.http.get<any>(environment.baseApiUrl + 'security-center/facilities/').subscribe({
      next: (response) => {
        if (response.status === 'ok' && response.data) {
          const facilities = response.data.map((f: any) => f.id);
          console.log('🏢 GLOBAL: Facilities cargadas:', facilities);
          this.websocketService.setFacilitiesToMonitor(facilities);
          this.facilitiesLoaded = true;
        }
        connectWebSocket();
      },
      error: (error) => {
        console.error('❌ GLOBAL: Error cargando facilities:', error);
        connectWebSocket();
      }
    });
  }

  private listenForPanicAlerts(): void {
    this.websocketService.panicAlerts$.subscribe(alert => {
      console.log('🚨 GLOBAL: Alerta recibida:', alert);
      if (alert) {
        // Verificar si la alerta es muy antigua (más de 5 horas)
        const alertTime = new Date(alert.created_at).getTime();
        const currentTime = Date.now();
        const fiveHoursInMs = 5 * 60 * 60 * 1000; // 5 horas en milisegundos
        const timeDifference = currentTime - alertTime;
        
        console.log('⏰ GLOBAL: Verificando antigüedad de alerta:', {
          alertTime: new Date(alert.created_at).toISOString(),
          currentTime: new Date(currentTime).toISOString(),
          timeDifferenceHours: (timeDifference / (60 * 60 * 1000)).toFixed(2),
          isOlderThan5Hours: timeDifference > fiveHoursInMs
        });
        
        if (timeDifference > fiveHoursInMs) {
          console.log('⚠️ GLOBAL: Alerta muy antigua (>5h), no reproducir sonido ni mostrar como urgente');
          // Marcar la alerta como antigua para que no se muestre como urgente
          alert.isOldAlert = true;
          this.activePanicAlertSubject.next(alert);
          // No reproducir sonido ni notificación para alertas antiguas
          return;
        }
        
        // Solo reproducir sonido y notificación si es nueva y no es antigua
        if (!this.processedAlerts.has(alert.id)) {
          console.log('🔊 GLOBAL: Reproduciendo alarma para nueva alerta');
          this.audioService.playAlarm();
          this.showBrowserNotification(alert);
          this.addProcessedAlert(alert.id);
        } else {
          console.log('🔇 GLOBAL: Alerta ya procesada, omitiendo sonido y notificación');
          // Aún así reproducir sonido si la alerta está activa (para casos de reconexión)
          if (alert.status === 'active') {
            console.log('🔊 GLOBAL: Alerta activa, reproduciendo sonido de todas formas');
            this.audioService.playAlarm();
          }
        }
        this.activePanicAlertSubject.next(alert);
      } else {
        console.log('ℹ️ GLOBAL: Alerta nula, deteniendo alarma');
        console.log('🔇 GLOBAL: Verificando si alarma está sonando:', this.audioService.isAlarmPlaying());
        
        // Detener alarma inmediatamente
        this.audioService.stopAlarm();
        
        // Si sigue sonando después de 1 segundo, forzar detención
        setTimeout(() => {
          if (this.audioService.isAlarmPlaying()) {
            console.log('🛑 GLOBAL: Alarma sigue sonando, forzando detención');
            this.audioService.forceStopAlarm();
          }
        }, 1000);
        
        this.activePanicAlertSubject.next(null);
      }
    });
    
    // Monitor connection status and reconnect if needed
    this.websocketService.connectionStatus$.subscribe(status => {
      console.log('🔌 GLOBAL: Estado de conexión:', status);
      if ((status === 'disconnected' || status === 'error') && this.initialized && this.isAuthenticated()) {
        console.log('🔄 GLOBAL: Reconectando WebSocket...');
        setTimeout(() => {
          this.ensureConnection();
        }, 2000);
      }
    });
  }

  // Method to ensure WebSocket connection when needed
  ensureConnection(): void {
    if (!this.isAuthenticated() || !this.initialized) {
      console.log('🚫 GLOBAL: Usuario no autenticado o servicio no inicializado');
      return;
    }
    
    const isConnected = this.websocketService.isConnected();
    console.log('🔍 GLOBAL: Verificando conexión - Conectado:', isConnected);
    
    if (!isConnected) {
      console.log('🔌 GLOBAL: Reconectando WebSocket...');
      // Only reconnect WebSocket, don't reload facilities
      if (this.facilitiesLoaded) {
        this.websocketService.connect();
      } else {
        this.loadFacilitiesAndConnect();
      }
    } else {
      console.log('✅ GLOBAL: WebSocket ya conectado');
    }
  }

  private loadProcessedAlerts(): void {
    try {
      const stored = localStorage.getItem(this.PROCESSED_ALERTS_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
        Object.keys(data).forEach(alertId => {
          if (data[alertId] > oneDayAgo) {
            this.processedAlerts.add(alertId);
          }
        });
      }
    } catch (error) {
      console.warn('Error loading processed alerts:', error);
    }
  }

  private addProcessedAlert(alertId: string): void {
    this.processedAlerts.add(alertId);
    this.saveProcessedAlerts();
  }

  private saveProcessedAlerts(): void {
    try {
      const data: { [key: string]: number } = {};
      this.processedAlerts.forEach(alertId => {
        data[alertId] = Date.now();
      });
      localStorage.setItem(this.PROCESSED_ALERTS_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Error saving processed alerts:', error);
    }
  }

  dismissAlert(): void {
    console.log('🔇 GLOBAL: Deteniendo alarma y limpiando alerta activa');
    console.log('🔇 GLOBAL: Estado de alarma antes:', this.audioService.isAlarmPlaying());
    
    // Detener alarma inmediatamente
    this.audioService.stopAlarm();
    
    // Verificar si se detuvo correctamente
    setTimeout(() => {
      if (this.audioService.isAlarmPlaying()) {
        console.log('🛑 GLOBAL: Alarma sigue sonando en dismissAlert, forzando detención');
        this.audioService.forceStopAlarm();
      }
    }, 500);
    
    this.activePanicAlertSubject.next(null);
    console.log('🔇 GLOBAL: Alerta limpiada');
  }

  forcePlayAlarm(): void {
    console.log('🔊 GLOBAL: Forzando reproducción de alarma');
    this.audioService.playAlarm();
  }

  getCurrentAlert(): PanicAlertData | null {
    return this.activePanicAlertSubject.value;
  }

  private isAuthenticated(): boolean {
    const token = sessionStorage.getItem('token');
    return !!token;
  }

  // Method to initialize service after login
  initializeAfterLogin(): void {
    if (!this.isAuthenticated()) {
      console.log('🚫 GLOBAL: Usuario no autenticado, no se puede inicializar');
      return;
    }
    
    if (this.initialized) {
      console.log('✅ GLOBAL: Servicio ya inicializado');
      return;
    }
    
    console.log('🔑 GLOBAL: Inicializando servicio después del login');
    this.initialized = true;
    this.requestNotificationPermission();
    this.loadFacilitiesAndConnect();
    this.startHeartbeat();
  }

  private async requestNotificationPermission(): Promise<void> {
    const granted = await this.browserNotificationService.requestPermission();
    if (granted) {
      console.log('🔔 GLOBAL: Permisos de notificación concedidos');
    } else {
      console.warn('⚠️ GLOBAL: Permisos de notificación denegados');
    }
  }

  private showBrowserNotification(alert: PanicAlertData): void {
    // Solo mostrar notificación si la ventana no está visible
    if (document.hidden) {
      console.log('🔔 GLOBAL: Mostrando notificación del navegador');
      this.browserNotificationService.showPanicAlert(alert.guard_name, alert.facility_name);
    } else {
      console.log('👁️ GLOBAL: Ventana visible, omitiendo notificación del navegador');
    }
  }

  // Test method to simulate panic alert
  testPanicAlert(): void {
    const testAlert: PanicAlertData = {
      id: 'test_global_' + Date.now(),
      guard_id: '999',
      guard_name: 'Test Guard Global',
      facility_id: '1',
      facility_name: 'Test Facility Global',
      alert_type: 'panic_button',
      description: 'Test panic alert from global service',
      priority: 'critical',
      created_at: new Date().toISOString()
    };

    console.log('🧪 GLOBAL TEST: Simulando alerta de pánico:', testAlert);
    console.log('🧪 GLOBAL TEST: Valor actual del subject:', this.activePanicAlertSubject.value);
    
    this.activePanicAlertSubject.next(testAlert);
    this.audioService.playAlarm();
    
    console.log('🧪 GLOBAL TEST: Nuevo valor del subject:', this.activePanicAlertSubject.value);
  }

  // Test method to simulate old panic alert
  testOldPanicAlert(): void {
    // Crear una fecha de hace 6 horas
    const sixHoursAgo = new Date(Date.now() - (6 * 60 * 60 * 1000));
    
    const testAlert: PanicAlertData = {
      id: 'test_old_global_' + Date.now(),
      guard_id: '888',
      guard_name: 'Test Guard Old Global',
      facility_id: '2',
      facility_name: 'Test Facility Old Global',
      alert_type: 'panic_button',
      description: 'Test old panic alert from global service (6h old)',
      priority: 'critical',
      created_at: sixHoursAgo.toISOString(),
      isOldAlert: true
    };

    console.log('🧪 GLOBAL TEST: Simulando alerta antigua:', testAlert);
    console.log('🧪 GLOBAL TEST: Valor actual del subject:', this.activePanicAlertSubject.value);
    
    this.activePanicAlertSubject.next(testAlert);
    // No reproducir sonido para alertas antiguas
    
    console.log('🧪 GLOBAL TEST: Nuevo valor del subject (sin sonido):', this.activePanicAlertSubject.value);
  }

  // Force show alert for testing
  forceShowAlert(): void {
    console.log('🔴 FORCE: Forzando mostrar alerta');
    const alert: PanicAlertData = {
      id: 'force_' + Date.now(),
      guard_id: '888',
      guard_name: 'Force Test',
      facility_id: '1',
      facility_name: 'Force Facility',
      alert_type: 'panic_button',
      description: 'Forced alert test',
      priority: 'critical',
      created_at: new Date().toISOString()
    };
    this.activePanicAlertSubject.next(alert);
  }

  // Method to cleanup service on logout
  cleanup(): void {
    console.log('🧹 GLOBAL: Limpiando servicio');
    this.initialized = false;
    this.facilitiesLoaded = false;
    this.isLoadingFacilities = false;
    this.stopHeartbeat();
    this.audioService.stopAlarm();
    this.activePanicAlertSubject.next(null);
    this.websocketService.disconnect();
  }
}